import React from 'react';
import { FiEdit2 as Edit, FiTrash2 as Trash2, FiAlertCircle as AlertCircle } from 'react-icons/fi';

interface TestCase {
  id: string;
  name: string;
  expected_output?: string;
  difficulty?: string;
  importance?: string;
  tags?: string[];
}

interface TestCaseTableProps {
  testCases: TestCase[];
  loading: boolean;
  onEdit: (tc: TestCase) => void;
  onDelete: (tc: TestCase) => void;
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
}

const TestCaseTable: React.FC<TestCaseTableProps> = ({
  testCases,
  loading,
  onEdit,
  onDelete,
  selectable = false,
  selectedIds = new Set(),
  onSelectionChange,
}) => {
  const allSelected =
    testCases.length > 0 && testCases.every((tc) => selectedIds.has(tc.id));
  const someSelected = testCases.some((tc) => selectedIds.has(tc.id));

  const toggleAll = () => {
    if (allSelected) {
      onSelectionChange?.(new Set());
    } else {
      onSelectionChange?.(new Set(testCases.map((tc) => tc.id)));
    }
  };

  const toggleOne = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange?.(next);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (testCases.length === 0) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          No test cases yet
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Get started by adding your first test case
        </p>
      </div>
    );
  }

  const getDifficultyBadgeClass = (difficulty: string | undefined): string => {
    const colors: Record<string, string> = {
      easy: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
      medium: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
      hard: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
    };
    return colors[difficulty ?? ''] ?? colors.medium;
  };

  const getImportanceBadgeClass = (importance: string | undefined): string => {
    const colors: Record<string, string> = {
      low: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
      medium: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
      high: 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200',
    };
    return colors[importance ?? ''] ?? colors.medium;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-900">
          <tr>
            {selectable && (
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el: HTMLInputElement | null) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={toggleAll}
                  className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                />
              </th>
            )}
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Difficulty
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Importance
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Tags
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {testCases.map((tc) => (
            <tr
              key={tc.id}
              className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              {selectable && (
                <td className="px-4 py-4 w-10">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(tc.id)}
                    onChange={() => toggleOne(tc.id)}
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                  />
                </td>
              )}
              <td className="px-6 py-4">
                <div className="text-sm font-medium text-gray-900 dark:text-white">{tc.name}</div>
                {tc.expected_output && (
                  <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-md">
                    {tc.expected_output.slice(0, 60)}
                    {tc.expected_output.length > 60 ? '...' : ''}
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getDifficultyBadgeClass(tc.difficulty)}`}
                >
                  {tc.difficulty}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getImportanceBadgeClass(tc.importance)}`}
                >
                  {tc.importance}
                </span>
              </td>
              <td className="px-6 py-4">
                <div className="flex gap-1 flex-wrap">
                  {(tc.tags?.length ?? 0) > 0 ? (
                    tc.tags!.slice(0, 2).map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 text-xs rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      >
                        {tag}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-gray-400">-</span>
                  )}
                  {(tc.tags?.length ?? 0) > 2 && (
                    <span className="text-xs text-gray-400">+{tc.tags!.length - 2}</span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => onEdit(tc)}
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(tc)}
                    className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TestCaseTable;
